import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wordmark } from "../components/BrandLogo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Auth() {
  const navigate = useNavigate();
  const { signup, login, linkAnon, profile } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        if (profile && profile.is_anonymous) {
          await linkAnon(email, password, null);
          toast.success(t("auth.linked"));
        } else {
          await signup(email, password, null);
          toast.success(t("auth.account_created"));
        }
      } else {
        await login(email, password);
        toast.success(t("auth.logged_in"));
      }
      navigate(profile?.onboarded ? "/today" : "/onboarding", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.generic_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ss-soft-bg min-h-[100dvh] w-full">
      <div className="mx-auto w-full max-w-md min-h-[100dvh] px-8 py-10 flex flex-col">
        <Wordmark className="mb-10" />
        <h1 className="font-serif text-3xl">
          {mode === "login" ? t("auth.welcome_back") : t("auth.create_account")}
        </h1>
        <p className="text-foreground/60 text-sm mt-2">
          {mode === "login" ? t("auth.sub_login") : t("auth.sub_signup")}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-foreground/60">{t("auth.email")}</Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-13 rounded-2xl bg-card border-border px-5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-foreground/60">{t("auth.password")}</Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-13 rounded-2xl bg-card border-border px-5"
            />
          </div>
          <Button
            data-testid="auth-submit-btn"
            disabled={busy}
            className="w-full h-14 rounded-full bg-brand hover:bg-brand/90 text-white text-base"
          >
            {busy ? t("common.loading") : mode === "login" ? t("auth.sign_in") : t("auth.create")}
          </Button>
        </form>

        <div className="mt-3 text-center">
          <button
            data-testid="auth-forgot-link"
            type="button"
            onClick={() => navigate("/forgot")}
            className="text-sm text-foreground/55 hover:text-brand transition-colors"
          >
            {t("auth.forgot")}
          </button>
        </div>

        <button
          data-testid="auth-toggle-mode"
          className="mt-4 text-sm text-foreground/60 hover:text-brand transition-colors"
          onClick={() => setMode(mode === "login" ? "signup" : "login")}
        >
          {mode === "login" ? t("auth.toggle_to_signup") : t("auth.toggle_to_login")}
        </button>

        <div className="mt-auto pt-10">
          <Link
            to="/welcome"
            data-testid="auth-back-link"
            className="text-sm text-foreground/50 hover:text-brand"
          >
            ← {t("common.back")}
          </Link>
        </div>
      </div>
    </div>
  );
}
